/* eslint:disable:radix */
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { chunk } from "lodash";

import { customRangeFormat, fullMonthAndYear, shortMonthAndYear } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { chosenSites } from "app/common/services/chosenSites";
import { isAvailable } from "app/common/services/pageClaims";
import { fetchComponents } from "app/common/services/swSettingsUtils";
import widgetConfig from "app/components/widget/widget-config/widget.config";
import { i18nFilter } from "app/filters/i18nFilter";
import { getDiffCustomRangeParam } from "app/helpers/duration/customRange";
import CountryService from "app/services/CountryService";
import type { ICountryObject } from "app/services/CountryService";
import {
    DefaultFetchService,
    FetchResponseError,
    NoCacheHeaders,
    type ResultWithFullResponse,
} from "app/services/fetchService";
import {
    defaultParams as sharedDefaultParams,
    monthIntervals as sharedMonthIntervals,
    monthIntervalsShort as sharedMonthIntervalsShort,
    monthIntervalsShortForAddOns as sharedMonthIntervalsShortForAddOns,
    weekIntervals as sharedWeekIntervals,
} from "app/Shared/defaults";
import type { ISubscriptionSolution } from "app/user-data/solutionBaseInterfaces";
import { isCCUserInTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialCheckUtils";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import type { DatePickerPreset, ISwSettings, ISwSites, ISwUrls } from "../../../app/@types/ISwSettings";

import PageVisibilityManager from "./pageVisibilityManager";

type DataType =
    | "MOBILE_WEB_ALGO_CHANGE"
    | "APPS_ALGO_CHANGE"
    | "KEYWORDS_ALGO_CHANGE"
    | "KEYWORDS_MOBILE_ALGO_CHANGE"
    | "MOBILE_WEB_DATA_START"
    | "APPS_SESSION_DATA_START"
    | "IOS_RANKING_POLICY_CHANGE"
    | "WEEKLY_DATA_AVAILABLE";

const modulesConfig: Record<string, { order: number; homeOrder: number }> = {
    Home: {
        order: 1,
        homeOrder: 1,
    },
    Dashboard: {
        order: 2,
        homeOrder: 2,
    },
    DeepInsights: {
        order: 3,
        homeOrder: 3,
    },
    WebAnalysis: {
        order: 5,
        homeOrder: 5,
    },
    IndustryAnalysis: {
        order: 6,
        homeOrder: 6,
    },
    KeywordAnalysis: {
        order: 7,
        homeOrder: 7,
    },
    AppAnalysis: {
        order: 8,
        homeOrder: 8,
    },
    AppKeywordAnalysis: {
        order: 10,
        homeOrder: 9,
    },
    TopApps: {
        order: 9,
        homeOrder: 10,
    },
};

const INTERNAL_USER_PLAN_NAME = "Internal Full Access";

interface IDataComponent {
    resources: {
        LastAvailableSnapshotData: string;
        LastAvailableWindowData: string;
    };
}

export class SWSettings implements ISwSettings {
    public static readonly DATA_COMPONENT_NAME = "DataSettings";

    public static readonly PING_INTERVAL = 12 * 60 * 60 * 1000; //12hr

    public static compareDates(component1: FixmeType, component2: FixmeType, field: string): boolean {
        const date1 = dayjs(component1.resources[field]);
        const date2 = dayjs(component2.resources[field]);
        return date1.isSame(date2, "day");
    }

    public components: any;

    public user: any;

    // Assigned via transformConfig() during init()
    public swsites!: ISwSites;

    // Assigned via transformConfig() during init()
    public swurls!: ISwUrls;

    public current: any;

    public widgets: any;

    public modules: any;

    // Assigned via transformConfig() during init()
    public version!: string;

    public subscriptionSolutions: ISubscriptionSolution[] = [];

    private translate = i18nFilter();

    // Assigned via transformConfig() during init()
    private dataComponent!: IDataComponent;

    private fetchService = DefaultFetchService.getInstance();

    private pvm: PageVisibilityManager = new PageVisibilityManager();

    private pvmKey: number | null = null;

    private pingDataComponentIntervalId: ReturnType<typeof setTimeout> | null = null;

    private lastHiddenTime = Date.now();

    private _ready = false;

    // Set via onReady setter; class behaves asynchronously
    private _onReady!: (ready: boolean) => void;

    constructor(
        private $window: Window,
        private defaultParams: FixmeType,
        private monthIntervals: FixmeType,
        private monthIntervalsShort: FixmeType,
        private weekIntervals = sharedWeekIntervals,
    ) {
        this.components = {};
    }

    public initiate(force?: boolean) {
        if (!this._onReady) {
            console.warn(
                "onReady callback is not set, this class behaves asynchronously, make sure to listen to ready state",
            );
        }
        if (this._ready && !force) {
            console.warn("Settings are already initiated, use initiate(true) to force re-initiate");
            return;
        }
        this.init();
    }

    public set onReady(clb: (ready: boolean) => void) {
        this._onReady = clb;
        if (this._ready) {
            this._onReady(this._ready);
        }
    }

    public get onReady() {
        return this._onReady;
    }

    private set ready(value: boolean) {
        this._ready = value;
        this.onReady?.(value);
    }

    private get ready() {
        return this._ready;
    }

    private validateAllowedCountries(allowedCountries: unknown[] | number | string) {
        if (Array.isArray(allowedCountries)) {
            return allowedCountries;
        }

        if (typeof allowedCountries === "number") {
            // Silent fix for the case when a number is passed instead of an array
            return [allowedCountries];
        }

        const typeofPassedItem = typeof allowedCountries;
        console.error(`filterCountries - Expected an array of allowed countries, but got ${typeofPassedItem} instead`);

        if (typeof allowedCountries === "string") {
            console.warn("Trying a saving operation by converting the allowedCountries to an array");

            const numericStr = parseInt(allowedCountries, 10);

            if (!isNaN(numericStr)) {
                return [numericStr];
            } else {
                console.error("filterCountries - Failed to convert the string to a number");
            }
        }

        return [];
    }

    public filterCountries(allowedCountries = [], excludeChildren = false) {
        const allowedCountriesValidated = this.validateAllowedCountries(allowedCountries);
        const allowedIds = new Set(allowedCountriesValidated);

        const result: ICountryObject[] = [];
        for (const country of CountryService.countries) {
            if (!allowedIds.has(country.id)) {
                continue;
            }
            if (excludeChildren) {
                result.push({ ...country, states: [], children: [] });
                continue;
            }
            const children = (country.states ?? []).filter((state: ICountryObject) => allowedIds.has(state.id));
            result.push({ ...country, children });
        }
        return result;
    }

    public initWidgetMetrics = (allMetrics: FixmeType): void => {
        this.widgets.metrics = allMetrics;
        if (!this.widgets.metrics) {
            Sentry.error(
                "initWidgetMetrics - No metrics defined in the widget config",
                new Error("No metrics defined"),
            );
            return;
        }
        // Set widget's country and date settings according to it's component settings
        Object.keys(this.widgets.metrics).forEach((metricId) => {
            const metric = this.widgets.metrics[metricId];
            if (!metric?.properties?.component) {
                return;
            }

            const component = this.components[metric.properties.component];
            if (!component) {
                Sentry.warning(
                    `${metric.properties.component} is not defined on your global scope. Please merge your sandbox with master, deploy, then try again.`,
                );
            }

            try {
                if (!component) {
                    throw new Error("Component not found");
                }

                metric.properties.minDate = component.startDate;
                metric.properties.maxDate = component.endDate;
                metric.properties.datePickerPresets = (component.datePickerPresets ?? []).map((preset: FixmeType) => {
                    const elem = { ...preset };
                    elem.buttonText = (elem.buttonText.indexOf("Last") === -1 ? "Last " : "") + elem.buttonText;
                    return elem;
                });
                metric.properties.countries = component.allowedCountries;
            } catch (error) {
                Sentry.errorTracker({
                    message: "Failed to load component",
                    e: error,
                    level: "error",
                    params: {
                        extra: {
                            component: metric.properties.component,
                        },
                    },
                });
            }
        });
    };

    private async loadConfigFromWindow() {
        return new Promise((resolve) => {
            if (window.similarweb?.settings) {
                return resolve(window.similarweb?.settings);
            }

            throw new Error("No settings object found on the window object - window.similarweb.settings is missing");
        }).then((settings) => {
            Object.assign(this, this.transformConfig(settings));
        });
    }

    private async init() {
        try {
            await this.loadConfigFromWindow();
            this.initDataComponentPing();
            this.initGAProperties();
            this.initUserProperties();
            this.ready = true;
        } catch (e) {
            Sentry.fatal("swSettings: error initializing settings: " + (e as Error).message, e);
        }
    }

    public initDataComponentPing(): void {
        if (this.pvmKey) {
            const success = this.pvm.unregisterCallback(this.pvmKey);

            if (!success) {
                Sentry.error(
                    "Failed to unregister the page visibility manager callback",
                    new Error(`Failed to unregister handlePageVisibilityChange callback with key ${this.pvmKey}`),
                );
            } else {
                this.pvmKey = null;
            }
        }

        if (this.pingDataComponentIntervalId) {
            clearInterval(this.pingDataComponentIntervalId);
            this.pingDataComponentIntervalId = null;
        }

        // check every interval
        this.pvmKey = this.pvm.registerCallback(this.handlePageVisibilityChange.bind(this));
    }

    public handlePageVisibilityChange(isVisible: boolean): void {
        if (isVisible) {
            // in case the page has been hidden longer than a single interval
            // we want to ping the data component immediately
            if (Date.now() - this.lastHiddenTime >= SWSettings.PING_INTERVAL) {
                this.pingDataComponent();
            }

            // then register the listener
            this.pingDataComponentIntervalId = setInterval(() => this.pingDataComponent(), SWSettings.PING_INTERVAL);
        } else if (this.pingDataComponentIntervalId) {
            // update the last hidden time
            // so we know if it's been longer than an interval when the page becomes visible again
            this.lastHiddenTime = Date.now();
            clearInterval(this.pingDataComponentIntervalId);
        }
    }

    public initGAProperties(): void {
        this.user.disableGA = this.components.Home.resources.DisableGA;
    }

    public initUserProperties(): void {
        this.user.isInternalUser = this.user.plan === INTERNAL_USER_PLAN_NAME;
    }

    public initPackageProperties(): void {
        const isSalesSolutionEnabled: boolean =
            this.components.SalesWorkspace?.resources?.SalesSolution2Enabled ||
            this.components.SalesWorkspace?.resources?.SalesIntelligenceAppsEnabled;

        this.user.hasProductOrClaimsOverride = this.hasOverriddenProductOrClaims();
        this.user.isSimilarWebUser = this.isSimilarWebUserWithoutOverride();
        this.user.isInternalUser = this.user.plan === INTERNAL_USER_PLAN_NAME;
        this.user.hasDM = isAvailable(this.components.DigitalMarketingProduct);
        this.user.hasMR = isAvailable(this.components.MarketResearchProduct);
        this.user.hasSlAccess = this.components.SalesWorkspace?.resources?.HasAccessToManageSuppressionList ?? false;
        this.user.hasCrmAccess = this.components.SalesWorkspace?.resources?.HasAccessToManageCrmIntegrations ?? false;
        this.user.hasApiSignalsAccess = this.components.SalesWorkspace?.resources?.HasAccessToManageApiSignals ?? false;
        this.user.hasSignalsRestApiAccess =
            this.components.SalesWorkspace?.resources?.HasAccessToManageSignalsRestApi ?? false;
        this.user.hasSI = this.user.isSimilarWebUser || isSalesSolutionEnabled;
        this.user.hasSolution2 = this.user.hasDM || this.user.hasMR || this.user.hasSI;
        this.user.hasSolution25 = this.components.Solution2NewNavigation?.resources?.AvaliabilityMode;
        this.user.hasSolution25OptIn = this.components.Solution2NewNavigationOptIn?.resources?.AvaliabilityMode;
        this.user.hasProductBoardAccess = isAvailable(this.components.ProductBoard);
        this.user.hasPptExport = isAvailable(this.components.ExportToPPT);
        this.user.hasNavAI = isAvailable(this.components.AINavWidget);
        this.user.hasDS = this.user.hasDM && this.user.hasMR; // Digital Suite users have both
        this.user.hasSwAppIntelligence =
            (this.components.AppAnalysisHome?.resources?.AvaliabilityMode &&
                isAvailable(this.components.AppAnalysisHome)) ||
            (this.components.WsAppPerformance?.resources?.AvaliabilityMode &&
                isAvailable(this.components.WsAppPerformance));
        this.user.applications = this.components.Home?.resources?.Application ?? [];
        this.user.hasAppsPremiumPlus = this.components.AppAnalysis?.resources?.IsAppPremiumPlus;
    }

    public momentFromString(yearMonthString: string) {
        const [year, month, day] = yearMonthString.split(".");
        // year + month + day
        if (day) {
            return dayjs
                .utc()
                .year(parseInt(year))
                .month(parseInt(month) - 1)
                .date(parseInt(day));
        }
        // year + month only
        else {
            return dayjs
                .utc()
                .year(parseInt(year))
                .month(parseInt(month) - 1);
        }
    }

    public async verifyOrFetchComponentById(component: Array<string>): Promise<void> {
        await this.fetchComponentConfigById(component);
    }

    public async setCurrent(component: Array<string>): Promise<void> {
        await this.fetchComponentConfigById(component);
        const componentToSetCurrent = Array.isArray(component) ? component[0] : component;
        this.current = this.components[componentToSetCurrent];
    }

    public getCurrentModule(): any {
        const component = this.getCurrentComponent();
        return this.modules.find(({ id }: FixmeType) => component.componentId === id);
    }

    public getCurrentComponent() {
        return this.current?.resources?.Home
            ? this.current
            : this.components[this.current?.resources?.module] || this.components[this.current?.componentId];
    }

    public getNavigationModule() {
        const component = this.getCurrentComponent();
        let componentId = component.componentId;
        if (componentId === "Dashboard") {
            componentId = "Home";
        }
        return this.modules.find(({ id }: FixmeType) => id === componentId);
    }

    public isCustomDuration(duration?: string) {
        const customDuration = duration ? duration.split("-") : [];
        return customDuration.length > 1;
    }

    public isCurrentDomainRelatedToUser() {
        const userSettings = this.user;
        const primaryDomain = chosenSites.getPrimarySite()?.name;
        return (
            userSettings.username.split("@")[1] === primaryDomain ||
            userSettings.customData.selectedWebApp === primaryDomain
        );
    }

    public isMonthByMonthPresetDuration(componentId?: string) {
        const component = (componentId && this.components[componentId]) || this.current;
        return component?.resources?.FixedSnapshotRangeLength === 1;
    }

    public allowedDuration(duration: string, componentId?: string, routerState?: IRouterState) {
        const component = (componentId && this.components[componentId]) || this.current;
        let start: Dayjs;
        let end: Dayjs;
        let returnedValue;
        // DMI-3068 Validate date For Monthly date picker used in Data 2.0 pages
        if (this.isMonthByMonthPresetDuration(componentId)) {
            const monthlyPreset = component.datePickerPresets.find((preset: FixmeType) => preset.value === duration);
            returnedValue = monthlyPreset?.enabled && !monthlyPreset?.locked;
            const overrideDatepickerPreset = routerState?.overrideDatepickerPreset;
            if (overrideDatepickerPreset) {
                returnedValue = returnedValue && overrideDatepickerPreset.includes(monthlyPreset.value);
            }
            return returnedValue;
        }
        if (component.resources.CustomDurations) {
            return component.resources.CustomDurations.indexOf(duration) > -1;
        }
        const isCustomDuration = this.isCustomDuration(duration);

        // custom duration
        if (isCustomDuration) {
            if (routerState?.hideCalendar) {
                returnedValue = false;
            } else {
                const customDuration = duration ? duration.split("-") : [];
                const [from, to] = customDuration;
                const isWeekly = from.split(".").length === 3 && to.split(".").length === 3;
                const granularity = isWeekly ? "isoWeek" : "month";
                start = this.momentFromString(from).startOf(granularity).clone();
                end = this.momentFromString(to).endOf(granularity).clone();
                const componentStartDate = isWeekly ? component.dailyStartDate : component.startDate;
                const componentEndDate = isWeekly ? component.dailyEndDate : component.endDate;

                returnedValue =
                    (start.isAfter(componentStartDate, granularity) || start.isSame(componentStartDate, granularity)) &&
                    (end.isBefore(componentEndDate, granularity) || end.isSame(componentEndDate, granularity));
            }
        }
        // preset
        else {
            const monthlyPreset = (component.datePickerPresets ?? []).find(
                (preset: FixmeType) => preset.value === duration,
            );
            const weeklyPreset = (component.weeklyDatePickerPresets ?? []).find(
                (preset: FixmeType) => preset.value === duration,
            );
            if (monthlyPreset) {
                returnedValue = monthlyPreset.enabled && !monthlyPreset.locked;
                const overrideDatepickerPreset = routerState?.overrideDatepickerPreset;
                if (overrideDatepickerPreset) {
                    returnedValue = returnedValue && overrideDatepickerPreset.includes(monthlyPreset.value);
                }
            } else if (weeklyPreset) {
                returnedValue = weeklyPreset.enabled && !weeklyPreset.locked;
            }
        }
        return returnedValue;
    }

    public allowedCountry(
        countryId: number | string,
        componentId: string | null = null,
        countriesList: number[] | null = null,
    ): boolean {
        const component = (componentId && this.components[componentId]) || this.current;
        const countriesListToUse = countriesList
            ? countriesList
            : Array.isArray(component.resources.Countries)
              ? component.resources.Countries
              : null;
        return countriesListToUse ? countriesListToUse.indexOf(parseInt(String(countryId), 10)) > -1 : false;
    }

    public async refresh(workerSettings = null): Promise<void> {
        let settings;
        if (!workerSettings) {
            // Legacy refresh intentionally posts a null body; cast satisfies the typed payload param.
            settings = await this.fetchService.post<{ data: any }>("/settings", null as never, {
                headers: NoCacheHeaders,
                preventAutoCancellation: true,
            });
            this.$window.similarweb.settings = settings;
        } else {
            this.$window.similarweb.settings = workerSettings;
        }
        this.init();
    }

    public async pingDataComponent(): Promise<void> {
        // To avoid cyclic dependency.
        try {
            const {
                result: { components },
            } = await this.fetchService.post<ResultWithFullResponse<{ components: any }>>(
                "/settings",
                [SWSettings.DATA_COMPONENT_NAME],
                { headers: NoCacheHeaders, preventAutoCancellation: true, withFullResponse: true },
            );
            try {
                const fetchedDataComponent: IDataComponent = components[SWSettings.DATA_COMPONENT_NAME];
                this.transformConfig(this.$window.similarweb.settings);
                if (
                    !SWSettings.compareDates(this.dataComponent, fetchedDataComponent, "LastAvailableSnapshotData") ||
                    !SWSettings.compareDates(this.dataComponent, fetchedDataComponent, "LastAvailableWindowData")
                ) {
                    location.reload();
                }
            } catch (e) {
                Sentry.error("Error in pingDataComponent", e);
            }
        } catch (errorObj: unknown) {
            if (errorObj instanceof FetchResponseError) {
                const isClientAbortedRequest = errorObj.response?.status === -1;
                const isAuthNeedRefresh = errorObj.response?.headers.get("SW-AuthNeedRefresh");

                if (!isClientAbortedRequest && !isAuthNeedRefresh) {
                    Sentry.errorTracker({
                        message: "Error in pingDataComponent",
                        e: new Error("Failed to ping data component", {
                            cause: `${errorObj.name}: ${errorObj.message}`,
                        }),
                        level: "error",
                        params: {
                            extra: {
                                status: errorObj.response?.status,
                                statusText: errorObj.response?.statusText,
                            },
                        },
                    });
                }
            }
        }
    }

    public getDataIndicators = (dataType: DataType) => {
        const dataTypes: Record<DataType, string> = {
            MOBILE_WEB_ALGO_CHANGE: this.components.Home.resources.MobileWebAlgoChangeDate,
            APPS_ALGO_CHANGE: this.components.Home.resources.MobileAppsAlgoChangeDate || "2020-11-01",
            KEYWORDS_ALGO_CHANGE: this.components.Home.resources.KeywordsAlgoChangeDate,
            KEYWORDS_MOBILE_ALGO_CHANGE: "2020-01-01",
            MOBILE_WEB_DATA_START: "2018-08-01",
            APPS_SESSION_DATA_START: "2021-06-01",
            IOS_RANKING_POLICY_CHANGE: "2021-09-02",
            WEEKLY_DATA_AVAILABLE: "2022-01-03",
        };
        return dataTypes[dataType];
    };

    public fetchComponentConfigById = async (componentId: Array<string>) => {
        // remove components we've already fetched
        const unfetchedComponents = componentId.filter(
            (comp) => !(comp in this.components) || !this.components[comp].full,
        );
        // if there's nothing left to fetch...
        if (unfetchedComponents.length === 0) {
            return;
        }
        const distinctUnFetchedComponents = Array.from(new Set(unfetchedComponents));
        const response = await fetchComponents(distinctUnFetchedComponents);
        this.updateComponentsStatus(unfetchedComponents, response);
    };

    public updateComponentsStatus = (unfetchedComponents: string[], response: FixmeType) => {
        unfetchedComponents.forEach((comp) => {
            const componentConfig = response.components[comp];
            // set the full property, so we know we have the full component config
            if (componentConfig) {
                componentConfig.full = true;
                this.processIndividualComponent(comp, componentConfig);
            }
        });
    };

    public isInternalUser = () => {
        return this.user.isInternalUser;
    };

    private processIndividualComponent(componentId: string, componentConfig: any): void {
        // Add the new component config we just fetched to the components collection
        this.components[componentId] = componentConfig;
        const configForTransform = {
            components: this.components,
            modules: this.modules,
        };

        const isFro = this.user.isFro;
        const isShortMonthIntervalsUser = this.user.isShortMonthIntervalsUser;
        this.processComponent(componentId, configForTransform, isFro, isShortMonthIntervalsUser);
    }

    private hasOverriddenProductOrClaims = (): boolean => {
        const hasProductsOverwritten = this.components.Home.resources.ProductsOverwritten;
        const hasClaimsOverwritten = this.components.Home.resources.ClaimsOverwritten;

        return Boolean(hasProductsOverwritten || hasClaimsOverwritten);
    };

    private isSimilarWebUserWithoutOverride = (): boolean => {
        const isSimilarWebUser = this.user.plan === INTERNAL_USER_PLAN_NAME;

        // a SW user without any claims manipulation
        return isSimilarWebUser && !this.hasOverriddenProductOrClaims();
    };

    private filterTotalCountries(totalCountries: number[] = [], addUSstates = false, supportedUsStates: number[] = []) {
        const totalIds = new Set(totalCountries);
        const stateIds = new Set(addUSstates ? supportedUsStates : totalCountries);

        const result: ICountryObject[] = [];
        for (const country of CountryService.countries) {
            if (!totalIds.has(country.id)) {
                continue;
            }
            if (!addUSstates) {
                result.push({ ...country, states: [], children: [] });
                continue;
            }
            const children = (country.states ?? []).filter((state: ICountryObject) => stateIds.has(state.id));
            result.push({ ...country, children });
        }
        return result;
    }

    private transformConfig(config: FixmeType) {
        if (!config) {
            Sentry.errorTracker({
                message: "No config object provided for transformConfig",
                e: new Error("Missing config object", { cause: `Expected config object but received ${config}` }),
            });
        }
        const { IsNoTouchUser, IsCommonUser } = config?.components?.Home?.resources ?? {};
        const isShortMonthIntervalsUser = IsNoTouchUser || IsCommonUser;
        config.user.isNoTouch = IsNoTouchUser;
        config.user.isFro = IsCommonUser;
        config.user.isShortMonthIntervalsUser = isShortMonthIntervalsUser;

        config.modules = [];
        Object.keys(config.components).forEach((componentId) => {
            this.processComponent(componentId, config, IsCommonUser, isShortMonthIntervalsUser);
        });
        config.widgets = widgetConfig.widgets;
        this.dataComponent = config.components[SWSettings.DATA_COMPONENT_NAME];
        return config;
    }

    public createMonthPresetToCalendar(
        month: number,
        component: FixmeType,
        supportedDuration: number,
        isFreshData: boolean,
        monthInterval: FixmeType,
    ): DatePickerPreset {
        const unit =
            month > 1
                ? this.translate("datepicker.units.month.nomerous")
                : this.translate("datepicker.units.month.single");
        const presetText = month + " " + unit;
        const endDate = component.endDate.clone();
        const endDateText = endDate.format(shortMonthAndYear);
        const isEnabled = month <= supportedDuration;

        return {
            buttonText: this.translate("datepicker.lastn", {
                count: month.toString(),
                unit,
            }),
            displayText: `${endDate
                .subtract(month - 1, "month")
                .format(shortMonthAndYear)} - ${endDateText}  (${presetText})`,
            value: month + "m",
            enabled: isEnabled,
            locked: isEnabled && (!isFreshData || month > monthInterval.count),
        };
    }

    private processComponent(
        componentId: string,
        config: FixmeType,
        isFro: boolean,
        isShortMonthIntervalsUser: boolean,
    ) {
        let module: any;
        const component = config.components[componentId];
        // component.privateResources = component.resources;
        component.resources.full = component.full;
        // component.resources = new Proxy(component.privateResources, this.resourcesHandler);

        //Those two properties required for Navigation Sidebar. IsDisabled arrives with minimal startupSettings request.
        component.isDisabled = component.resources.IsDisabled;
        component.isAllowed = !component.isDisabled;

        // if the component has full=false
        // no need to parse the component at this point
        // it will be lazy loaded later
        if (component && component.resources && component.full) {
            // TODO: remove this with proxies
            // component.full = component.resources.full = true;
            let monthInterval = component.resources.SnapshotInterval;
            const windowInterval = component.resources.WindowInterval;
            const dateRangeEnabled = component.resources.DateRangeEnabled;
            const dailyInterval = component.resources.DailyInterval;
            const weeklyGranularity = component.resources.WeeklyGranularity;
            // HOTFIX 25/05/2017 by Yoav.Shmaria - Pro is not loading when TopApps.resources.SnapshotInterval is not defined
            if (monthInterval === undefined) {
                monthInterval = config.components.Home.resources.SnapshotInterval;
            }

            const supportedDuration = component.resources.SupportedDuration || monthInterval.count;

            /* Some FRO users get old data, we should lock presets for those */
            const lastAvailableSnapshotData = config.components.DataSettings.resources.LastAvailableSnapshotData;
            const isFreshData =
                !isFro ||
                Math.abs(dayjs.utc(monthInterval.enddate).diff(dayjs.utc(lastAvailableSnapshotData), "days")) <=
                    dayjs.utc(dayjs.utc(lastAvailableSnapshotData).month()).daysInMonth();

            if (!component.resources.type) {
                // not a WebComponent
                return;
            }

            // id
            component.componentId = componentId;

            // expose startdate and enddate per component
            component.startDate = dayjs.utc(monthInterval.startdate);
            component.endDate = dayjs.utc(monthInterval.enddate);

            component.lastSupportedDailyDate = dayjs.utc(component.resources.LastSupportedDailyDate);

            // set allowed countries
            component.allowedCountries = this.filterCountries(
                component.resources.Countries,
                !component.resources.USStateSupported,
            );
            if (component.resources.CountriesAndroid) {
                component.allowedAndroidCountries = this.filterCountries(
                    component.resources.CountriesAndroid,
                    !component.resources.USStateSupported,
                );
            }
            if (component.resources.CountriesIOS) {
                component.allowedIOSCountries = this.filterCountries(
                    component.resources.CountriesIOS,
                    !component.resources.USStateSupported,
                );
            }
            component.totalCountries = this.filterTotalCountries(
                component.resources.TotalCountries,
                component.resources.USStateSupported,
                component.resources.UsStates,
            );

            // set allowed stores
            component.allowedStores = component.resources.Stores;

            // set properties for compare competitors popup
            component.totalCompetitorsCount = component.resources.TotalCompetitorsCount;
            component.allowedCompetitorsCount = component.resources.AllowedCompetitorsCount;

            // set properties for each mode
            component.isBeta = component.resources.IsBeta;
            component.isHaveBanner = component.resources.IsHaveBanner;

            component.isDemo = component.resources.IsDemo;
            component.IsPaymentRequired = component.resources.IsPaymentRequired;
            component.durationPaymentHook = component.resources.DurationPaymentHook;
            component.tableResults = component.resources.TableResults;
            // component.isInactive = component.resources.IsInactiveAccountUser;

            component.disabledDomains = {
                includeSubdomains: false,
                mainDomainOnly: component.resources.IsMainDomainDisabled,
            };

            // set presets for date picker
            // if this components defined with fixed duration length, for example: Digital marketing -> Competitive analysis -> ranking distribution,
            // The preset is built depends on the FixedSnapshotRangeLength, Otherwise it built depends on
            // Predefined values (1,3,6,12,18,24 etc);
            if (typeof component.resources.FixedSnapshotRangeLength === "number") {
                component.datePickerPresets = [];
                const range = component.resources.FixedSnapshotRangeLength;
                const ranges = chunk(Array.from(Array(monthInterval.count).keys()), range);
                ranges.forEach((currentRange) => {
                    const from = component.endDate.clone().subtract(currentRange[currentRange.length - 1], "month");
                    const to = component.endDate.clone().subtract(currentRange[0], "month");

                    if (range == 1) {
                        component.datePickerPresets.push({
                            buttonText: from.format(fullMonthAndYear),
                            displayText: from.format(fullMonthAndYear),
                            value: from.format(`${customRangeFormat}-${customRangeFormat}`),
                            enabled: true,
                            locked: false,
                        });
                    } else {
                        component.datePickerPresets.push({
                            buttonText: `${from.format(fullMonthAndYear)}-${to.format(fullMonthAndYear)}`,
                            displayText: `${from.format(fullMonthAndYear)}-${to.format(fullMonthAndYear)}`,
                            value: `${from.format(customRangeFormat)}-${to.format(customRangeFormat)}`,
                            enabled: true,
                            locked: false,
                        });
                    }
                });
                if (component.resources.SnapshotInterval.count < component.resources.SupportedDuration) {
                    component.datePickerPresets.push({
                        buttonText: i18nFilter()("historical.data"),
                        displayText: i18nFilter()("historical.data"),
                        value: "12m",
                        enabled: true,
                        locked: true,
                    });
                }
                if ((range == 1 && component.resources?.WindowInterval) || componentId === "KeywordAnalysisV2Organic") {
                    component.datePickerPresets.unshift({
                        buttonText: i18nFilter()("datepicker.lastn", {
                            count: "28",
                            unit: i18nFilter()("datepicker.units.day.nomerous"),
                        }),
                        displayText: i18nFilter()("datepicker.lastn", {
                            count: "28",
                            unit: i18nFilter()("datepicker.units.day.nomerous"),
                        }),
                        value: "28d",
                        enabled: true,
                        locked: component.resources.WindowInterval?.locked || false,
                    });
                }
            } else {
                component.datePickerPresets = [];
                const isAddonAvailable = isAvailable(this.components.AllowHistoricalDataAddon);
                const hasTrialDaysLeft = isCCUserInTrial(this.user?.customData?.quiz?.CcTrialStartDate);
                const isAfterTrialUser: boolean = this.components.Home.resources.IsAfterTrial ?? false;
                let monthIntervals;
                switch (true) {
                    case isAddonAvailable && !hasTrialDaysLeft:
                        monthIntervals = sharedMonthIntervalsShortForAddOns;
                        break;
                    case isShortMonthIntervalsUser:
                        monthIntervals = this.monthIntervalsShort;
                        break;
                    default:
                        monthIntervals = this.monthIntervals;
                        break;
                }
                monthIntervals.forEach((val: number) => {
                    const newMonthInterval: DatePickerPreset = this.createMonthPresetToCalendar(
                        val,
                        component,
                        supportedDuration,
                        isFreshData,
                        monthInterval,
                    );
                    component.datePickerPresets.push(newMonthInterval);
                });
                component.datePickerPresets.unshift({
                    buttonText: i18nFilter()("datepicker.lastn", {
                        count: "28",
                        unit: i18nFilter()("datepicker.units.day.nomerous"),
                    }),
                    displayText: i18nFilter()("datepicker.lastn", {
                        count: "28",
                        unit: i18nFilter()("datepicker.units.day.nomerous"),
                    }),
                    value: "28d",
                    enabled: false,
                    locked: isAfterTrialUser || !isFreshData,
                });
            }

            component.graphGranularities = {
                Daily: !component.resources.IsDailyGraphDataForbidden,
                Weekly: true,
                Monthly: true,
            };
            if (windowInterval) {
                if (windowInterval !== "Locked" && !windowInterval.locked) {
                    // Due to a bug in the Pro BE, we want to move from setting windowInterval to "Locked"
                    // to returning a full interval object with the locked property set to true
                    // In order to not create a FE/BE deployment race condition
                    // we'll support both for backward compatibility
                    // and remove the old condition
                    component.windowStartDate = dayjs.utc(windowInterval.startdate);
                    component.windowEndDate = dayjs.utc(windowInterval.enddate);
                    component.datePickerPresets[0].displayText +=
                        " (As of " + component.windowEndDate.format("MMM DD") + ")";
                } else {
                    component.datePickerPresets[0].locked = true;
                }
                component.datePickerPresets[0].enabled = true;
            }

            // generate weekly data presets
            component.weeklyDatePickerPresets = [];
            if (weeklyGranularity || dailyInterval) {
                component.dailyStartDate = dayjs.utc(dailyInterval.startdate);
                component.dailyEndDate = dayjs.utc(dailyInterval.enddate);
            }
            if (weeklyGranularity && dailyInterval) {
                this.weekIntervals.forEach((week) => {
                    const unit =
                        week > 1
                            ? i18nFilter()("datepicker.units.week.nomerous")
                            : i18nFilter()("datepicker.units.week.single");
                    const presetText = `${week} ${unit}`;
                    const isoWeekStart = component.dailyEndDate.clone().subtract(week, "weeks").isoWeek();
                    const isoWeekEnd = component.dailyEndDate.clone().subtract(1, "weeks").isoWeek();
                    const startDateText = dayjs.utc().isoWeek(isoWeekStart).startOf("isoWeek").format("MMM DD, YY");
                    const endDateText = dayjs.utc().isoWeek(isoWeekEnd).endOf("isoWeek").format("MMM DD, YY");
                    component.weeklyDatePickerPresets.push({
                        buttonText: i18nFilter()("datepicker.previousn", {
                            count: week.toString(),
                            unit,
                        }),
                        displayText: `${startDateText} - ${endDateText} (${presetText})`,
                        value: week + "w",
                        // since all users has at least 3 months of data
                        enabled: true,
                        locked: false,
                    });
                });
                component.weeklyDatePickerPresets.unshift({
                    buttonText: i18nFilter()("datepicker.lastn", {
                        count: "7",
                        unit: i18nFilter()("datepicker.units.day.nomerous"),
                    }),
                    displayText: `${i18nFilter()("datepicker.lastn", {
                        count: "7",
                        unit: i18nFilter()("datepicker.units.day.nomerous"),
                    })} (As of ${component.dailyEndDate.format("MMM DD")})`,
                    value: "7d",
                    enabled: true,
                    locked: false,
                });
            }

            // add default route params
            component.defaultParams = Object.assign(
                {},
                this.defaultParams,
                component.resources.InitialDuration
                    ? {
                          duration: isFreshData
                              ? component.resources.InitialDuration + component.resources.DurationUnit
                              : getDiffCustomRangeParam(dayjs(monthInterval.startdate), dayjs(monthInterval.enddate)),
                      }
                    : {},
                component.resources.InitialCountry ? { country: component.resources.InitialCountry } : {},
                component.resources.InitialCategory ? { category: component.resources.InitialCategory } : {},
                component.resources.InitialPage ? { pageUrl: component.resources.InitialPage } : {},
                component.resources.InitialStore ? { store: component.resources.InitialStore } : {},
                component.resources.InitialDevice ? { device: component.resources.InitialDevice } : {},
                component.resources.InitialMode ? { mode: component.resources.InitialMode } : {},
            );

            // create the default duration for components with fixed duration length
            if (typeof component.resources.FixedSnapshotRangeLength === "number") {
                const to = dayjs.utc(monthInterval.enddate).startOf("month");
                const from = to.clone().subtract(component.resources.FixedSnapshotRangeLength - 1, "month");
                component.defaultParams.duration = `${from.format(customRangeFormat)}-${to.format(customRangeFormat)}`;
            }

            // add demo properties
            component.demo = {};
            if (component.resources.DemoAllowedWebsite) {
                component.demo.website = component.resources.DemoAllowedWebsite;
            }
            if (component.resources.DemoAllowedWebsiteCompetitors) {
                component.demo.websiteCompetitors = component.resources.DemoAllowedWebsiteCompetitors;
            }
            if (component.resources.DemoAllowedAppCompetitors) {
                component.demo.appsCompetitors = component.resources.DemoAllowedAppCompetitors;
            }
            if (component.resources.DemoAllowedApp) {
                component.demo.appId = component.resources.DemoAllowedApp;
            }
            if (component.resources.DemoAllowedKeyword) {
                component.demo.keyword = component.resources.DemoAllowedKeyword;
            }
            if (component.isDemo && component.resources.DemoAllowedCategory) {
                component.demo.category = component.resources.DemoAllowedCategory;
                Object.assign(component.defaultParams, {
                    category: component.resources.DemoAllowedCategory,
                });
            }
        }

        // Populate modules for menu and homepage
        if (component.resources.Home) {
            module = {
                id: componentId,
                name: component.resources.name,
                type: component.resources.type,
                caption: component.resources.caption,
                description: i18nFilter()(component.resources.description),
                order: modulesConfig[componentId] ? modulesConfig[componentId].order : null,
                homeOrder: modulesConfig[componentId] ? modulesConfig[componentId].homeOrder : null,
                url: component.resources.defaulturl,
                isDemo: component.isDemo,
                isHaveBanner: component.isHaveBanner,
                isBeta: component.isBeta,
                disabledDomains: component.disabledDomains,
                graphGranularities: component.graphGranularities,
                hasNewBadge: component.resources.HasNewBadge,
            };
            config.modules.push(module);
        }
    }
}

const createSwSettings = () => {
    // Return a proxy that delays initialization until access
    // This allows static imports without accessing window.similarweb immediately
    let instance: SWSettings | null = null;

    const getInstance = () => {
        if (!instance) {
            instance = new SWSettings(
                typeof window !== "undefined" ? window : ({} as any),
                sharedDefaultParams,
                sharedMonthIntervals,
                sharedMonthIntervalsShort,
                sharedWeekIntervals,
            );
        }
        return instance;
    };

    return new Proxy({} as SWSettings, {
        get: (_target, prop) => {
            const settings = getInstance();
            const value = (settings as any)[prop];
            if (typeof value === "function") {
                return value.bind(settings);
            }
            return value;
        },
        set: (_target, prop, value) => {
            const settings = getInstance();
            (settings as any)[prop] = value;
            return true;
        },
        has: (_target, prop) => {
            return prop in getInstance();
        },
        ownKeys: () => {
            return Reflect.ownKeys(getInstance());
        },
        getOwnPropertyDescriptor: (_target, prop) => {
            const descriptor = Reflect.getOwnPropertyDescriptor(getInstance(), prop);

            if (!descriptor) {
                return undefined;
            }

            return {
                ...descriptor,
                configurable: true,
            };
        },
    });
};

export let swSettings = createSwSettings();

export const mockSettings = (mock: FixmeType) => {
    // For testing purposes, we can replace the instance
    const settings = swSettings as unknown as SWSettings; // It's actually the proxy, but acts like SWSettings
    Object.assign(settings, mock);
};

export const resetSettings = () => {
    swSettings = createSwSettings();
};
